import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

document.addEventListener('DOMContentLoaded', function () {
  const header = document.getElementById('ssSiteHeader');
  if (!header) return;

  /* ===================== Hide-on-scroll-down / reveal-on-scroll-up =====================
     Horizon 2025+ scrolls `.page-wrapper` (desktop) or `document.scrollingElement` (mobile)
     instead of `window` — window.scrollY never changes on desktop, so binding to `window`
     silently never fires. Bind to the theme's own scroll-container utilities (same ones
     assets/header.js uses) and rebind when the breakpoint switches the scroll target.

     The actual class-toggle work is rAF-throttled (at most once per animation frame,
     however many raw 'scroll' events fire in between) and uses a hysteresis band
     (hideThreshold) around a direction anchor instead of comparing to the immediately
     previous sample — without both of these, tiny sub-pixel deltas from momentum/trackpad
     scrolling flip ss-hide on and off every few pixels, which the .3s CSS transition on
     header.ss-header renders as a visible flicker, and the unthrottled getBoundingClientRect
     read below ran on every single scroll event and was a measurable source of scroll jank. */
  const threshold = 24;
  const hideThreshold = 6;
  let lastY = getScrollTop();
  let directionAnchorY = lastY;
  let scrollContainer = getScrollEventTarget();
  let ticking = false;

  function updateHeader() {
    const y = getScrollTop();
    header.classList.toggle('ss-scrolled', y > threshold);

    const delta = y - directionAnchorY;
    if (y <= threshold) {
      header.classList.remove('ss-hide');
      directionAnchorY = y;
    } else if (delta > hideThreshold) {
      header.classList.add('ss-hide');
      directionAnchorY = y;
    } else if (delta < -hideThreshold) {
      header.classList.remove('ss-hide');
      directionAnchorY = y;
    }
    lastY = y;

    // Dark-red header hover/active is only meaningful from the top of the homepage down
    // through the Shop Siscode section. Past its bottom edge (quote section onward), mark
    // <body> so site-header.css can force the neutral gray back — this also overrides any
    // "active" class left stuck on the Shop link by the Home/Shop observer below, which only
    // fires on entering a section and never explicitly clears it on leaving.
    const shopSectionEl = document.getElementById('shop');
    if (shopSectionEl) {
      const belowShop = shopSectionEl.getBoundingClientRect().bottom <= 0;
      document.body.classList.toggle('ss-below-shop', belowShop);
    }

    ticking = false;
  }

  function handleScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateHeader);
  }

  scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
  scrollContainerMediaQuery.addEventListener('change', function () {
    scrollContainer.removeEventListener('scroll', handleScroll);
    scrollContainer = getScrollEventTarget();
    lastY = getScrollTop();
    directionAnchorY = lastY;
    ticking = false;
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
  });

  /* ===================== Mobile drawer / scrim ===================== */
  const scrim = document.getElementById('ssScrim');
  const mobileDrawer = document.getElementById('ssMobileDrawer');
  const accountDropdown = document.getElementById('ssAccountDropdown');

  function closeAllPanels() {
    mobileDrawer.classList.remove('open');
    accountDropdown.classList.remove('open');
    scrim.classList.remove('open');
  }
  function openDrawer() {
    closeAllPanels();
    mobileDrawer.classList.add('open');
    scrim.classList.add('open');
  }

  document.getElementById('ssHamburgerBtn').addEventListener('click', openDrawer);
  document.getElementById('ssDrawerClose').addEventListener('click', closeAllPanels);
  scrim.addEventListener('click', closeAllPanels);
  mobileDrawer.querySelectorAll('a').forEach((a) => a.addEventListener('click', closeAllPanels));

  /* ===================== Account dropdown ===================== */
  document.getElementById('ssAccountToggle').addEventListener('click', function (e) {
    e.stopPropagation();
    const willOpen = !accountDropdown.classList.contains('open');
    closeAllPanels();
    if (willOpen) accountDropdown.classList.add('open');
  });
  document.addEventListener('click', function (e) {
    if (!accountDropdown.contains(e.target)) accountDropdown.classList.remove('open');
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllPanels();
  });

  /* ===================== Cart icon opens Horizon's real cart drawer ===================== */
  document.getElementById('ssCartToggle').addEventListener('click', function () {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else {
      window.location.href = '/cart';
    }
  });

  /* ===================== Live cart count via Shopify's standard cart events ===================== */
  const cartCountEl = document.getElementById('ssCartCount');
  import('@shopify/events')
    .then(({ StandardEvents }) => {
      document.addEventListener(StandardEvents.cartLinesUpdate, function (event) {
        event.promise
          ?.then(({ cart }) => {
            if (!cart) return;
            const count = cart.totalQuantity ?? 0;
            cartCountEl.textContent = count;
            cartCountEl.style.display = count > 0 ? 'flex' : 'none';
          })
          .catch(() => {});
      });
    })
    .catch(() => {});

  /* ===================== Home/Shop active nav state (homepage only) ===================== */
  const navHome = document.querySelector('.ss-nav-home');
  const navShop = document.querySelector('.ss-nav-shop');
  const drawerHome = document.querySelector('.ss-drawer-home');
  const drawerShop = document.querySelector('.ss-drawer-shop');
  const homeSec = document.getElementById('home');
  const shopSec = document.getElementById('shop');

  if (navHome && navShop && homeSec && shopSec) {
    function setActive(which) {
      navHome.classList.toggle('active', which === 'home');
      navShop.classList.toggle('active', which === 'shop');
      if (drawerHome) drawerHome.classList.toggle('active', which === 'home');
      if (drawerShop) drawerShop.classList.toggle('active', which === 'shop');
    }
    const io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) setActive(en.target.id);
        });
      },
      { threshold: 0.5 }
    );
    io.observe(homeSec);
    io.observe(shopSec);
  }
});
